import throttle from 'lodash/throttle';

import type {
  GetTicketApiArg,
  GetWorkflowApiArg,
} from '~/api/k2/typedefs.autogen';
import type { RootState } from '~/rootState';

import { type TagName, Tags } from '~/api/k2/tags.autogen';
import {
  pusherStore,
  registerPusherResync,
} from '~/common/contexts/pusherStore';
import { PusherEventType } from '~/enums/PusherEventType';
import { k2 } from '~/redux/api/k2';
import { logger } from '~/utils/logger';
import { captureMessage } from '~/utils/sentry';

import {
  type RealtimeTag,
  scopeListTagsToTicket,
  ticketScopedListTags,
} from './realtimeTags';

type K2ChannelKind = 'ticket' | 'workflow';

/**
 * A single user action can emit a burst of CDC events (a stage transition
 * touches the ticket, its stages, its tasks and each signing configuration), so
 * invalidation is coalesced over a short window. lodash's default leading +
 * trailing edges are both wanted here: the leading edge still fires immediately
 * — delaying the first dispatch would add latency rather than remove it — and
 * the trailing edge absorbs the rest of the burst.
 */
const COALESCE_WINDOW_MS = 200;

/**
 * Pusher does not replay dropped events, and a drop that happens while the
 * socket stays nominally healthy produces no reconnect to resync from. This
 * low-frequency refetch of the subscribed resource bounds how long a missed
 * event can leave the view stale.
 *
 * The timer restarts on every real signal (a Pusher event, a reconnect
 * resync, or the previous backstop tick itself), so it only ever fires this
 * long after the last thing that already refreshed the resource — a real
 * event never gets shadowed by a redundant tick a few seconds later.
 */
const BACKSTOP_REFETCH_MS = 60_000;

/**
 * How long the page must stay hidden before the backstop pauses. Without this,
 * a tab left open (and thus a cache entry kept alive) overnight polls every
 * `BACKSTOP_REFETCH_MS` indefinitely, for a user who isn't looking at it. The
 * grace period avoids pausing on an incidental tab switch.
 */
const IDLE_PAUSE_DELAY_MS = 2 * 60_000;

type PageIdleListener = (isIdle: boolean) => void;

const pageIdleListeners = new Set<PageIdleListener>();
let hiddenTimeoutId: number | null = null;
let isPageIdle = false;

const notifyPageIdleListeners = (idle: boolean) => {
  isPageIdle = idle;
  pageIdleListeners.forEach((listener) => listener(idle));
};

const handlePageVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    hiddenTimeoutId = window.setTimeout(() => {
      hiddenTimeoutId = null;
      notifyPageIdleListeners(true);
    }, IDLE_PAUSE_DELAY_MS);
    return;
  }

  if (hiddenTimeoutId !== null) {
    window.clearTimeout(hiddenTimeoutId);
    hiddenTimeoutId = null;
  }
  if (isPageIdle) {
    notifyPageIdleListeners(false);
  }
};

/**
 * Subscribe to the page's idle state, derived from the Page Visibility API:
 * idle once the page has been continuously hidden for `IDLE_PAUSE_DELAY_MS`,
 * active again as soon as it becomes visible. A single shared listener backs
 * every subscriber — mirrors the per-resource channel coordinator, where many
 * cache entries share one underlying subscription.
 *
 * Re-syncing against the live `document.visibilityState` on the first
 * subscriber (rather than trusting the cached `isPageIdle`) matters because the
 * shared listener is detached whenever the last subscriber unsubscribes: a page
 * already hidden when the first-ever subscriber arrives would otherwise never
 * start the idle countdown, and a page that became visible again while nobody
 * was subscribed would otherwise leave a stale "idle" flag that wrongly pauses
 * the next subscriber.
 */
const subscribeToPageIdle = (listener: PageIdleListener): (() => void) => {
  if (pageIdleListeners.size === 0) {
    document.addEventListener('visibilitychange', handlePageVisibilityChange);
    handlePageVisibilityChange();
  }
  pageIdleListeners.add(listener);

  return () => {
    pageIdleListeners.delete(listener);
    if (pageIdleListeners.size === 0) {
      document.removeEventListener(
        'visibilitychange',
        handlePageVisibilityChange,
      );
      if (hiddenTimeoutId !== null) {
        window.clearTimeout(hiddenTimeoutId);
        hiddenTimeoutId = null;
      }
    }
  };
};

/**
 * Build the Pusher channel name for a K2 resource.
 *
 * The channel is namespaced with the owning tenant
 * (`private-k2-{kind}-{clientId}-{id}`) so subscription auth can bind it to the
 * caller's tenant and reject cross-tenant subscriptions. Returns `null` when the
 * client id is unknown: there is no un-namespaced channel to fall back to, and
 * subscribing to a malformed name would fail auth on every attempt.
 *
 * The client id is passed in (read from the RTK Query lifecycle `getState`)
 * rather than importing the store singleton, which would create a circular
 * import: `~/store` → `~/redux/api/k2` → this module → `~/store`.
 */
const buildK2ChannelName = (
  kind: K2ChannelKind,
  resourceId: string,
  clientId: number | undefined,
): string | null => {
  if (!Number.isFinite(clientId)) {
    return null;
  }
  return `private-k2-${kind}-${clientId}-${resourceId}`;
};

/** The resource's own tag — the minimum needed to re-establish truth about it. */
const resourceTag = (kind: K2ChannelKind, resourceId: string): RealtimeTag =>
  kind === 'workflow'
    ? { type: Tags.Workflow, id: resourceId }
    : { type: Tags.Ticket, id: resourceId };

/**
 * Tags to refetch after the connection has been re-established. Unlike an event,
 * a reconnect carries no information about what changed, so the resource and
 * everything scoped to it are refreshed.
 */
const resyncTags = (kind: K2ChannelKind, resourceId: string): RealtimeTag[] =>
  kind === 'workflow'
    ? [resourceTag(kind, resourceId)]
    : [
        resourceTag(kind, resourceId),
        { type: Tags.TicketActions, id: resourceId },
        ...ticketScopedListTags(resourceId),
      ];

/**
 * Every K2 Pusher event carries an `invalidation_tags` array — RTK Query
 * cache-invalidation tags derived by the backend from the changed DB columns.
 * The tag `type` strings match our `Tags` vocabulary exactly, so they feed
 * straight into `k2.util.invalidateTags(...)`.
 */
type K2UpdatedEvent = {
  invalidation_tags?: Array<{ type?: string; id?: string }>;
};

type Dispatch = (action: ReturnType<typeof k2.util.invalidateTags>) => void;

type K2CacheLifecycle = {
  cacheDataLoaded: Promise<void>;
  cacheEntryRemoved: Promise<void>;
  dispatch: Dispatch;
  getState: () => RootState;
};

const KNOWN_TAGS = new Set<string>(Object.values(Tags));

/**
 * Map the raw `invalidation_tags` from a Pusher event into typed RTK tags,
 * dropping any tag whose `type` is not part of our known `Tags` vocabulary
 * (defensive against backend additions the UI doesn't yet model — an unknown
 * tag matches no cached query anyway).
 */
const readInvalidationTags = (event: K2UpdatedEvent): RealtimeTag[] =>
  (event.invalidation_tags ?? [])
    .filter(
      (tag): tag is { type: string; id?: string } =>
        typeof tag?.type === 'string' && KNOWN_TAGS.has(tag.type),
    )
    .map((tag) => ({ type: tag.type as TagName, id: tag.id }));

/**
 * Per-channel realtime coordination, shared by every cache-entry lifecycle bound
 * to the same resource.
 *
 * Distinct RTK Query cache keys for one resource each run `onCacheEntryAdded`
 * against the same `private-k2-{kind}-{clientId}-{id}` channel — `getTicket({ ticketId })`
 * and `getTicket({ ticketId, include: ['workflow'] })` are both live on the
 * ticket route. Owning the subscription, the coalescing window, the reconnect
 * resync registration and the backstop timer here rather than per cache entry
 * means one event produces one `invalidateTags` dispatch and one backstop tick
 * per resource, however many entries are open. Refcounting keeps the channel
 * alive until the last lifecycle releases it, so removing one unused entry
 * cannot blind the others.
 */
type ChannelCoordinator = {
  refCount: number;
  teardown: () => void;
};

const channelCoordinators = new Map<string, ChannelCoordinator>();

/**
 * Test-only: tear down and drop every channel coordinator between cases.
 *
 * Tears down rather than just clearing the map, so a test that fails before its
 * cache entry is released cannot leave a live interval or Pusher subscription
 * behind to bleed into the next case.
 */
export function resetK2ChannelCoordinatorsForTests() {
  channelCoordinators.forEach((coordinator) => coordinator.teardown());
  channelCoordinators.clear();
  if (hiddenTimeoutId !== null) {
    window.clearTimeout(hiddenTimeoutId);
    hiddenTimeoutId = null;
  }
  isPageIdle = false;
}

const createChannelCoordinator = (
  channelName: string,
  kind: K2ChannelKind,
  resourceId: string,
  eventType: PusherEventType,
  dispatch: Dispatch,
): ChannelCoordinator => {
  const channel = pusherStore.subscribe(pusherStore.getPusher(), channelName, {
    success: () => {
      logger.pusher('subscribed %s', channelName);
    },
    error: (subscriptionError) => {
      logger.pusher(
        'subscription failed %s %o',
        channelName,
        subscriptionError,
      );
      captureMessage(`Pusher subscription failed for ${channelName}`, {
        section: 'pusher subscription',
        resource_kind: kind,
        subscription_status: String(subscriptionError?.status),
      });
    },
  });

  const pendingTags = new Map<string, RealtimeTag>();

  const flushInvalidation = () => {
    if (pendingTags.size === 0) return;
    const tags = [...pendingTags.values()];
    pendingTags.clear();
    logger.pusher('%s → invalidating %o', channelName, tags);
    dispatch(k2.util.invalidateTags(tags));
  };

  const flushInvalidationThrottled = throttle(
    flushInvalidation,
    COALESCE_WINDOW_MS,
  );

  const queueTags = (tags: RealtimeTag[]) => {
    for (const tag of tags) {
      pendingTags.set(`${tag.type}:${tag.id ?? ''}`, tag);
    }
    flushInvalidationThrottled();
  };

  let backstopTimeoutId: number | null = null;
  let backstopPaused = false;

  const scheduleBackstop = () => {
    if (backstopTimeoutId !== null) {
      window.clearTimeout(backstopTimeoutId);
      backstopTimeoutId = null;
    }
    if (backstopPaused) return;
    backstopTimeoutId = window.setTimeout(() => {
      queueTags([resourceTag(kind, resourceId)]);
      scheduleBackstop();
    }, BACKSTOP_REFETCH_MS);
  };

  const unsubscribeFromPageIdle = subscribeToPageIdle((idle) => {
    backstopPaused = idle;
    if (idle) {
      if (backstopTimeoutId !== null) {
        window.clearTimeout(backstopTimeoutId);
        backstopTimeoutId = null;
      }
      return;
    }

    // The page just came back: refetch immediately rather than waiting up to
    // BACKSTOP_REFETCH_MS, since the socket may have been throttled while
    // backgrounded and silently missed an event.
    queueTags([resourceTag(kind, resourceId)]);
    scheduleBackstop();
  });

  // Read after subscribing: joining as the first subscriber synchronizes
  // `isPageIdle` against the live document state (see `subscribeToPageIdle`),
  // so this reflects reality rather than a value that predates that sync.
  backstopPaused = isPageIdle;
  scheduleBackstop();

  const onUpdated = (event: K2UpdatedEvent) => {
    logger.pusher('%s → %s %o', channelName, eventType, event);

    const tags = readInvalidationTags(event);
    if (tags.length > 0) {
      queueTags(
        kind === 'ticket' ? scopeListTagsToTicket(resourceId, tags) : tags,
      );
      scheduleBackstop();
    }
  };
  channel.bind(eventType, onUpdated);

  const unregisterResync = registerPusherResync(() => {
    logger.pusher('resyncing %s after reconnect', channelName);
    queueTags(resyncTags(kind, resourceId));
    scheduleBackstop();
  });

  return {
    refCount: 0,
    teardown: () => {
      channel.unbind(eventType, onUpdated);
      unregisterResync();
      unsubscribeFromPageIdle();
      if (backstopTimeoutId !== null) {
        window.clearTimeout(backstopTimeoutId);
      }
      flushInvalidationThrottled.flush();
      pusherStore.getPusher().unsubscribe(channelName);
      logger.pusher('unsubscribed %s', channelName);
    },
  };
};

/**
 * RTK Query `onCacheEntryAdded` core for real-time K2 updates via Pusher.
 *
 * Joins (or creates) the resource's channel coordinator when the cache entry is
 * created and releases it when the entry is removed, keeping the subscription
 * alive independent of component lifecycle.
 *
 * On each update event the backend-supplied `invalidation_tags` are dispatched to
 * `k2.util.invalidateTags`, letting RTK Query refetch exactly the affected
 * queries. The backend owns what to invalidate, so there are no client-side
 * per-column heuristics here.
 */
const bindK2Invalidation = async (
  kind: K2ChannelKind,
  resourceId: string,
  eventType: PusherEventType,
  { cacheDataLoaded, cacheEntryRemoved, dispatch, getState }: K2CacheLifecycle,
) => {
  await cacheDataLoaded;

  // `currentUser` starts as `{}`, but it is always populated by the time a k2
  // query can resolve: the router only mounts once the splash gate in
  // `App.tsx` clears, and that gate waits on LaunchDarkly `identify()` keyed on
  // `currentUser.client` plus subdomain resolution, which is dispatched after
  // `setCurrentUser`. So this branch means something is genuinely wrong (an
  // unauthenticated deep link cannot reach it — `cacheDataLoaded` only resolves
  // on a fulfilled query) and it must report rather than retry.
  const channelName = buildK2ChannelName(
    kind,
    resourceId,
    getState().currentUser.client,
  );
  if (!channelName) {
    logger.pusher(
      'skipped %s %s subscription: no client id on currentUser',
      kind,
      resourceId,
    );
    captureMessage(`Pusher ${kind} subscription skipped: unknown client id`, {
      section: 'pusher subscription',
      resource_kind: kind,
    });
    return;
  }

  const existing = channelCoordinators.get(channelName);
  if (existing) {
    existing.refCount += 1;
  } else {
    // Only the first lifecycle's `dispatch` is captured. Every lifecycle
    // dispatches to the same store, so which one the coordinator holds is
    // immaterial — and it outlives any single cache entry by design.
    const coordinator = createChannelCoordinator(
      channelName,
      kind,
      resourceId,
      eventType,
      dispatch,
    );
    coordinator.refCount = 1;
    channelCoordinators.set(channelName, coordinator);
  }

  await cacheEntryRemoved;

  const coordinator = channelCoordinators.get(channelName);
  if (!coordinator) return;

  coordinator.refCount -= 1;
  if (coordinator.refCount > 0) {
    logger.pusher(
      'released %s (refcount=%s)',
      channelName,
      coordinator.refCount,
    );
    return;
  }

  channelCoordinators.delete(channelName);
  coordinator.teardown();
};

export const onTicketK2CacheEntryAdded = (
  arg: GetTicketApiArg,
  api: K2CacheLifecycle,
) =>
  bindK2Invalidation(
    'ticket',
    arg.ticketId,
    PusherEventType.TicketUpdated,
    api,
  );

export const onWorkflowK2CacheEntryAdded = (
  arg: GetWorkflowApiArg,
  api: K2CacheLifecycle,
) =>
  bindK2Invalidation(
    'workflow',
    arg.workflowId,
    PusherEventType.WorkflowUpdated,
    api,
  );
